import { makeBrandColors } from 'hsds/utils/color'
import { useMemo } from 'react'
import type { ReactNode } from 'react'
import { ThemeProvider } from 'styled-components'

interface BrandThemeProviderProps {
  brandColor?: string
  children: ReactNode
}

// `makeBrandColors` throws on invalid input; our contract validates the hex
// before this point, but guard anyway so a bad value degrades to default
// styling instead of blanking the portal.
const safeMakeBrandColors = (brandColor: string) => {
  try {
    return makeBrandColors(brandColor)
  } catch {
    return null
  }
}

/**
 * Applies the tenant brand color to the portal by deriving a contrast-safe
 * palette with hsds `makeBrandColors` — the same util Beacon themes with — and
 * exposing it on the styled-components theme as `brandColor`. Components read it
 * via `getThemeBrandProp(props, 'textColor', fallback)`.
 *
 * With no (or invalid) brand color, children render in default Help Scout
 * styling: v1 portals ship unbranded until a tenant sets a color.
 */
export const BrandThemeProvider = ({
  brandColor,
  children,
}: BrandThemeProviderProps) => {
  // Always render the same ThemeProvider element (never swap to a Fragment) so
  // children aren't remounted when the brand color appears or changes; merge
  // the derived palette into any outer theme, or pass the outer theme through
  // unchanged when there's no (or an invalid) brand color.
  const theme = useMemo(() => {
    const brandColors = brandColor ? safeMakeBrandColors(brandColor) : null
    return (outer = {}) =>
      brandColors ? { ...outer, brandColor: brandColors } : outer
  }, [brandColor])

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}
